import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../utils/contactInfo';
import styles from './Auth.module.scss';
import { StaticPageLayout } from '../StaticPages/StaticPageLayout';

interface Props {
  email: string;
}

export const ConfirmEmail: React.FC<Props> = ({ email }) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (resendError) {
        setError(resendError.message);
      } else {
        setResent(true);
      }
    } catch {
      setError('Could not resend the email. Please try again in a moment.');
    } finally {
      setResending(false);
    }
  };

  return (
    <StaticPageLayout title="Check your email">
      <div className={styles.authContainer} style={{ minHeight: 'auto', background: 'transparent' }}>
        <div
          className={styles.card}
          style={{ maxWidth: '480px', margin: '0 auto', border: 'none', boxShadow: 'none', background: 'transparent' }}
        >
          <p className={styles.subText} style={{ marginBottom: '1rem' }}>
            We sent a confirmation link to <strong>{email}</strong>. Click the link to verify your email and finish setting up your account.
          </p>
          <p className={styles.subText} style={{ fontSize: '0.875rem' }}>
            The link will open MyMinu and sign you in. You can close this tab.
          </p>

          {resent && (
            <div
              className={styles.error}
              role="status"
              aria-live="polite"
              style={{ background: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.2)', color: '#059669' }}
            >
              Confirmation email re-sent. Please check your inbox.
            </div>
          )}

          {error && (
            <div className={styles.error} role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <button
            type="button"
            className={styles.button}
            onClick={handleResend}
            disabled={resending || resent}
          >
            {resending ? 'Sending…' : resent ? 'Email sent ✓' : 'Resend confirmation email'}
          </button>

          <div className={styles.link} style={{ marginTop: '1.5rem' }}>
            <Link to="/login">Back to login</Link>
          </div>
          <div className={styles.link} style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Need help? <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
};
